import { useEffect, useRef, useState } from 'react';
import Modal from './ui/Modal.jsx';
import { pb } from '../lib/pocketbase.js';
import { DOKUMENT_TYP_OPTIONS } from '../lib/dokument.js';
import { formatDateigroesse } from '../lib/format.js';

const EMPTY = {
  typ: 'angebot',
  titel: '',
  notiz: '',
};

export default function DokumentFormModal({ open, onClose, onSaved, praxisId }) {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setFile(null);
      setError(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  function handleChange(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  function handleFileChange(e) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    // Wenn noch kein Titel gesetzt ist, automatisch den Dateinamen ohne
    // Endung übernehmen — typische CRM-Convenience.
    if (f && !form.titel) {
      const base = f.name.replace(/\.[^.]+$/, '');
      setForm((prev) => ({ ...prev, titel: base }));
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (!file) {
        throw new Error('Bitte eine Datei auswählen.');
      }

      // PocketBase-File-Upload braucht FormData mit allen Feldern.
      const formData = new FormData();
      formData.append('praxis', praxisId);
      formData.append('typ', form.typ);
      formData.append('titel', form.titel || file.name);
      formData.append('notiz', form.notiz || '');
      formData.append('datei', file);
      formData.append('dateiname', file.name);
      formData.append('dateigroesse', String(file.size));

      const saved = await pb.collection('dokumente').create(formData);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(err?.message || 'Upload fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Neues Dokument hochladen" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="label">Typ *</label>
            <select required className="input mt-1" value={form.typ} onChange={handleChange('typ')}>
              {DOKUMENT_TYP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Titel *</label>
            <input
              type="text"
              required
              className="input mt-1"
              value={form.titel}
              onChange={handleChange('titel')}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="label">Datei *</label>
            <input
              ref={fileInputRef}
              type="file"
              required
              onChange={handleFileChange}
              className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
            />
            {file && (
              <p className="mt-1 text-xs text-slate-500">
                {file.name} · {formatDateigroesse(file.size)}
              </p>
            )}
            <p className="mt-1 text-xs text-slate-500">Maximale Größe: 50 MB.</p>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Notiz</label>
            <textarea
              rows={3}
              className="input mt-1"
              value={form.notiz}
              onChange={handleChange('notiz')}
            />
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}

        <div className="sticky bottom-0 -mx-6 -mb-5 mt-6 flex justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Lade hoch …' : 'Hochladen'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
